import React, { useState, useMemo, useCallback } from 'react';
import { C, S } from '../../utils/styles';
import { fmt, fmtK } from '../../utils/formatters';
import { useConfig } from '../../context/ConfigContext';
import { useClients } from '../../context/ClientsContext';
import { computeIBOS, toggleLayerEnabled, updateLayerInput } from '../../utils/ibosCalculations';
import IBOSSummary from './IBOSSummary';
import IBOSLayerTable from './IBOSLayerTable';
import IBOSLayerCard from './IBOSLayerCard';
import IBOSComparison from './IBOSComparison';
import Icon from '../Icon';

/**
 * IBOS multi-layer view. Consumes the workforce analysis totals and renders
 * the full IBOS optimization opportunity across 8 layers.
 *
 * Props:
 *  - totals: workforce totals (count, eligible, emprFica, empBenefit, hcRevenue, byPlan)
 *  - analyzed: analyzed employees array (used to derive avgAnnualSalary)
 *  - companyName: for header display
 */
export default function IBOSView({ totals, analyzed, companyName }) {
  const { config } = useConfig();
  const { activeClient, updateCurrentClient, saveClient } = useClients();

  const ibosLayers = useMemo(() => config?.ibosLayers || [], [config]);
  const clientIbosConfig = useMemo(() => activeClient?.ibosConfig || {}, [activeClient]);

  const avgAnnualSalary = useMemo(() => {
    if (!analyzed || analyzed.length === 0) return 0;
    const sum = analyzed.reduce((s, e) => s + (Number(e.salary) || 0), 0);
    return Math.round(sum / analyzed.length);
  }, [analyzed]);

  const { layers: layerResults, aggregates } = useMemo(
    () => computeIBOS({ workforceTotals: totals || {}, avgAnnualSalary, ibosLayers, clientIbosConfig }),
    [totals, avgAnnualSalary, ibosLayers, clientIbosConfig]
  );

  const [expandedLayerId, setExpandedLayerId] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const handleToggleLayer = useCallback(
    async (layerId, nextEnabled) => {
      const updated = toggleLayerEnabled(clientIbosConfig, layerId, nextEnabled);
      // Update in-memory first for instant feedback
      updateCurrentClient({ ibosConfig: updated });
      // Persist silently if this is a saved client
      if (activeClient?.id) {
        try {
          setSavingToggle(true);
          await saveClient({ ...activeClient, ibosConfig: updated });
        } catch (err) {
          console.error('Failed to persist IBOS toggle:', err);
        } finally {
          setSavingToggle(false);
        }
      }
    },
    [clientIbosConfig, updateCurrentClient, activeClient, saveClient]
  );

  const handleLayerInputChange = useCallback(
    async (layerId, inputKey, value) => {
      const updated = updateLayerInput(clientIbosConfig, layerId, inputKey, value);
      updateCurrentClient({ ibosConfig: updated });
      if (activeClient?.id) {
        try {
          await saveClient({ ...activeClient, ibosConfig: updated });
        } catch (err) {
          console.error('Failed to persist IBOS input:', err);
        }
      }
    },
    [clientIbosConfig, updateCurrentClient, activeClient, saveClient]
  );

  if (!totals || totals.count === 0) {
    return (
      <div style={{ ...S.card, textAlign: 'center', padding: '48px 24px' }}>
        <Icon name="information-circle-outline" style={{ fontSize: 36, color: C.textLight, marginBottom: 12 }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>
          Upload a workforce census to view IBOS
        </h3>
        <p style={{ fontSize: 13, color: C.textLight, margin: 0 }}>
          The Integrated Benefits Optimization Strategy uses your census data to project multi-layer savings and value.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ibos-view" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <IBOSSummary aggregates={aggregates} companyName={companyName} employeeCount={totals.count} />

      <IBOSLayerTable
        layers={layerResults}
        onToggle={handleToggleLayer}
        onSelectLayer={(id) => setExpandedLayerId(expandedLayerId === id ? null : id)}
        expandedLayerId={expandedLayerId}
        saving={savingToggle}
      />

      <IBOSComparison aggregates={aggregates} />

      <div style={{ ...S.card }}>
        <h3 style={S.heading}>Layer Details</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {layerResults.map((layer) => (
            <IBOSLayerCard
              key={layer.id}
              layer={layer}
              expanded={expandedLayerId === layer.id}
              onToggleExpand={() => setExpandedLayerId(expandedLayerId === layer.id ? null : layer.id)}
              onToggleEnabled={(nextEnabled) => handleToggleLayer(layer.id, nextEnabled)}
              onInputChange={(inputKey, value) => handleLayerInputChange(layer.id, inputKey, value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
